import cn from 'classnames';
import {FC} from 'react';
import {AlfaBankBadgeIcon, RocketBadgeIcon} from 'shared/assets/icons';
import {
	getWorkStatusConnectionSubtitle,
	getWorkStatusPartnerLabel,
	isWorkStatusConnectedAppearance,
	isWorkStatusErrorState,
	isWorkStatusPendingState,
	RegistrationWorkStatus,
	shouldShowAlfaBankBadge,
	withWorkStatusPartnerLabel,
	WORK_STATUS_ICONS,
	WorkStatusConnectionState,
} from '../../../model';
import styles from './WorkStatusTile.module.css';

type Props = {
	value: RegistrationWorkStatus;
	label: string;
	selected: boolean;
	connectionState?: WorkStatusConnectionState;
	disabled?: boolean;
	subtitleOverride?: string;
	/**
	 * Подпись и бейдж партнёра.
	 * `none` — без подписи и бейджа (free_smz).
	 * Без значения у СМЗ Альфа бейдж считается сам.
	 */
	partnerBadge?: 'alfa' | 'rocket' | 'none';
	onSelect: (value: RegistrationWorkStatus) => void;
};

export const WorkStatusTile: FC<Props> = ({
	value,
	label,
	selected,
	connectionState = 'available',
	disabled = false,
	subtitleOverride,
	partnerBadge,
	onSelect,
}) => {
	const Icon = WORK_STATUS_ICONS[value];
	const hidePartnerSignature = partnerBadge === 'rocket' || partnerBadge === 'none';
	const subtitle = withWorkStatusPartnerLabel(
		getWorkStatusPartnerLabel(partnerBadge, value),
		subtitleOverride ?? getWorkStatusConnectionSubtitle(connectionState),
	);
	const isConnectedLook = isWorkStatusConnectedAppearance(connectionState);
	const isCurrent = connectionState === 'current';
	const isPending = isWorkStatusPendingState(connectionState);
	const isError = isWorkStatusErrorState(connectionState);
	const showAlfaBadge = hidePartnerSignature
		? false
		: partnerBadge === 'alfa' || shouldShowAlfaBankBadge(value, connectionState);
	const showRocketBadge = partnerBadge === 'rocket' && !isError && !isPending;

	return (
		<button
			type="button"
			className={cn(styles.tile, isCurrent && styles.tile_current)}
			aria-pressed={selected || isCurrent}
			disabled={disabled}
			onClick={() => onSelect(value)}
		>
			<span
				className={cn(
					styles.iconWrap,
					isConnectedLook && styles.iconWrap_connected,
					!isConnectedLook && connectionState === 'available' && styles.iconWrap_available,
					isPending && styles.iconWrap_pending,
					isError && styles.iconWrap_error,
				)}
			>
				{isCurrent ? (
					<span className={styles.badge} aria-hidden>
						<svg className={styles.badgeIcon} viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path
								d="M1 4.2L3.8 7L9 1"
								stroke="white"
								strokeWidth="1.5"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					</span>
				) : null}
				{showAlfaBadge ? <AlfaBankBadgeIcon className={styles.partnerBadge} /> : null}
				{showRocketBadge ? <RocketBadgeIcon className={styles.partnerBadge} /> : null}
				<Icon className={styles.icon} />
			</span>

			<span className={styles.textBlock}>
				<span className={styles.label}>{label}</span>
				{subtitle ? (
					<span
						className={cn(
							styles.subtitle,
							subtitleOverride && styles.subtitle_error,
							!subtitleOverride && isPending && styles.subtitle_pending,
							!subtitleOverride && isError && styles.subtitle_error,
						)}
					>
						{subtitle}
					</span>
				) : null}
			</span>
		</button>
	);
};
